import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import styled from 'styled-components';
import {
  SELECTED_RAMP,
  brand,
  ink,
  motion,
  radius,
  rgba,
  shadow,
  space,
  surface,
} from '@repo/design-tokens';
import { Button, Icon } from '@repo/design-system';
import { prefersReducedMotion, useDragPosition } from '@repo/ui';
import type { DragPosition } from '@repo/ui';

import {
  PLAN_DEFAULT_SIZE,
  fromFraction,
  toFraction,
  type CardFraction,
  type CardId,
  type CardPositions,
  type CardSize,
} from './cardPositions';
import {
  CORNERS,
  clampSize,
  cursorFor,
  sizeLimits,
  type CardRect,
  type Corner,
} from './cornerResize';
import { useCornerResize } from './useCornerResize';

export type CardHome = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/** Each home corner as a fraction of the card's free travel — what "no stored entry" means. */
const HOME_FRACTION: Record<CardHome, CardFraction> = {
  'top-left': { fx: 0, fy: 0 },
  'top-right': { fx: 1, fy: 0 },
  'bottom-left': { fx: 0, fy: 1 },
  'bottom-right': { fx: 1, fy: 1 },
};

/** The cards' clearance from the picture's edge, as `camera-stage` placed them (`space[3]`). */
const INSET = parseInt(space[3], 10);
/** Arrow keys move 8 px, Shift+arrows 32 px (FR4). */
const KEY_STEP = { step: 8, bigStep: 32 };
const HOME_MS = parseFloat(motion.durMid) * 1000;

/** The grip keys that move or reset a card (`useDragPosition`): inert while a corner is dragged. */
const GRIP_MOVE_KEYS = new Set(['Home', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);

const sameFraction = (a: CardFraction, b: CardFraction) => a.fx === b.fx && a.fy === b.fy;

/** A card's clearance from each edge of its layer, in px (`homeInset`). */
export interface CardInset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const DEFAULT_INSET: CardInset = { top: INSET, right: INSET, bottom: INSET, left: INSET };

export interface MovableCardProps {
  id: CardId;
  home: CardHome;
  /** The card's name in *Move {name}* and *{Name} back in its corner*. */
  name: string;
  /**
   * The cards layer: the bounds a card moves in, and what its fractions are of. An element held in
   * the parent's STATE, not a ref: the layer is this card's parent, and a parent's ref is attached
   * only after its children's layout effects have run, so a ref would read empty on the first
   * measurement. Set from a callback ref, the state update re-renders before paint.
   */
  layer: HTMLDivElement | null;
  /** Where the home outlines go — the first child of the layer, so they paint behind every card. */
  outlineHost: HTMLElement | null;
  positions: CardPositions;
  /** The id of the layer's one *Arrow keys move it. Home puts it back.* description. */
  describedById: string;
  /** Says a sentence through the stage's one polite region (the host's announcer). */
  announce: (text: string) => void;
  /**
   * `inline` (default): the grip is handed to `children` to place in the card's own header.
   * `corner`: the slot draws it, top-left, as a white disc — for the corner plan, which the stage
   * portals into the slot and so cannot take a grip as a child.
   */
  gripPlacement?: 'inline' | 'corner';
  /**
   * Attributes for an empty element inside the slot that another tree portals its card into — the
   * corner plan's `data-camera-corner`. Its own element, so the slot's React children (the grip)
   * and the portalled card never share one DOM node.
   */
  portalTargetProps?: Record<string, string>;
  /** Told the slot's measured size when it changes (the dock's, which the shortcut card sits above). */
  onSize?: (size: { w: number; h: number }) => void;
  /**
   * The card can be resized from any corner after a short rest there (`observers-corner-plan-resize`)
   * — the corner plan only. The size is the portal target's, so this needs `portalTargetProps`.
   */
  resizable?: boolean;
  /** A resizable card's size until resized, and its smallest. */
  defaultSize?: CardSize;
  /**
   * The card's clearance from each edge of the layer, in px, for where its fractions (and so its
   * home) fall (`onboarding-persistent-scene-camera-feeds-glass`, R6.1): Camera Feeds' card sits
   * below the nav bar and at the 8 px edge. Default `space[3]` on every side. The drag and resize
   * clamps keep `space[3]`, so a card let go nearer an edge than this settles at it.
   */
  homeInset?: CardInset;
  /** Told whether the card is at its home, on mount and on each change (the host's rail follows it). */
  onAtHomeChange?: (atHome: boolean) => void;
  /**
   * The slot's smallest height, as a CSS length (`floor-plan-item-cards`, R2): set on the slot and
   * marked with `data-min-height`, so the host's layer can let the card inside grow to fill it.
   * Unset (the default), the slot is its card's own height.
   */
  minHeight?: string;
  children: (grip: React.ReactNode) => React.ReactNode;
}

/**
 * One card over the camera picture that can be moved (`observers-page-motion`, FR1–FR7): a slot in
 * the cards layer, positioned from the person's stored fraction or its home corner, dragged by a
 * grip (`useDragPosition`), sent home by a double-click or Home on the grip, and outlined at home
 * while it is away and being moved.
 *
 * The slot is positioned by `left`/`top` once measured. Before that first measurement — one layout
 * effect, before paint — it sits in its home corner by CSS, exactly where `camera-stage` put it. A
 * resting slot takes NO z-index, so a tooltip inside one card can still rise over another; only the
 * card being dragged lifts, to `z-index: 1`, inside the layer (never the legacy 1000 band — the
 * Remove Camera confirm and the toasts stay above every card, FR7).
 */
export const MovableCard: React.FC<MovableCardProps> = ({
  id,
  home,
  name,
  layer,
  outlineHost,
  positions,
  describedById,
  announce,
  gripPlacement = 'inline',
  portalTargetProps,
  onSize,
  resizable = false,
  defaultSize = PLAN_DEFAULT_SIZE,
  homeInset = DEFAULT_INSET,
  onAtHomeChange,
  minHeight,
  children,
}) => {
  const { top: inTop, right: inRight, bottom: inBottom, left: inLeft } = homeInset;
  const slotRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(layer);
  layerRef.current = layer;
  const stored = positions.get(id);
  const fraction = stored ?? HOME_FRACTION[home];
  const atHome = stored === undefined || sameFraction(stored, HOME_FRACTION[home]);
  const storedSize = resizable ? positions.getSize(id) : undefined;
  /** Resized away from the default: the reset has something to put back (FR20). */
  const sized =
    storedSize !== undefined && (storedSize.w !== defaultSize.w || storedSize.h !== defaultSize.h);

  /** Where the fraction puts the card, and where home is, for the layer and card as they are now. */
  const [layout, setLayout] = useState<{
    at: DragPosition;
    home: DragPosition;
    size: { w: number; h: number };
    layer: { width: number; height: number };
  } | null>(null);

  const measure = useCallback(() => {
    const slot = slotRef.current;
    if (!layer || !slot) return;
    const { width, height } = layer.getBoundingClientRect();
    const w = slot.offsetWidth;
    const h = slot.offsetHeight;
    // An empty slot (the plan not portalled in yet) is not measured: it stays in its CSS corner.
    if (w === 0 || h === 0) {
      setLayout(null);
      return;
    }
    const homeF = HOME_FRACTION[home];
    const next = {
      at: {
        x: fromFraction(fraction.fx, width, w, inLeft, homeF.fx, inRight),
        y: fromFraction(fraction.fy, height, h, inTop, homeF.fy, inBottom),
      },
      home: {
        x: fromFraction(homeF.fx, width, w, inLeft, homeF.fx, inRight),
        y: fromFraction(homeF.fy, height, h, inTop, homeF.fy, inBottom),
      },
      size: { w, h },
      layer: { width, height },
    };
    setLayout(prev =>
      prev &&
      prev.at.x === next.at.x &&
      prev.at.y === next.at.y &&
      prev.home.x === next.home.x &&
      prev.home.y === next.home.y &&
      prev.size.w === w &&
      prev.size.h === h &&
      prev.layer.width === width &&
      prev.layer.height === height
        ? prev
        : next,
    );
  }, [layer, home, fraction.fx, fraction.fy, inTop, inRight, inBottom, inLeft]);

  // Before paint, and whenever the layer or the fraction changes.
  useLayoutEffect(measure, [measure]);
  /** A corner resize is under way (set below, once the resize hook has run). */
  const resizingRef = useRef(false);

  // The window, the step list, a banner or a new picture resizes the layer; the card's content
  // resizes the card. Either way the same fraction is re-applied (FR9) — in the same frame: the
  // observer runs before paint, and a plain state update from it would paint once at the old place.
  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    // While a corner resize is under way the drag places and sizes the card itself; re-measuring
    // inside the observer then would resize it again within the same delivery — the browser's
    // "ResizeObserver loop" error on every frame. The release re-measures (the fraction changes).
    const observer = new ResizeObserver(() => {
      if (!resizingRef.current) flushSync(measure);
    });
    if (layer) observer.observe(layer);
    if (slotRef.current) observer.observe(slotRef.current);
    return () => observer.disconnect();
  }, [layer, measure]);

  const w = layout?.size.w;
  const h = layout?.size.h;
  useLayoutEffect(() => {
    if (w !== undefined && h !== undefined) onSize?.({ w, h });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, h]);

  useEffect(() => {
    onAtHomeChange?.(atHome);
    // Told on a change of place only; a new callback identity is not a change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atHome]);

  const [homing, setHoming] = useState(false);
  const homingTimer = useRef<number | null>(null);
  useLayoutEffect(
    () => () => {
      if (homingTimer.current !== null) window.clearTimeout(homingTimer.current);
    },
    [],
  );

  const sendHome = useCallback(() => {
    // A resized card at its home position still has its size to put back, in the same step (FR20).
    if (atHome && !sized) return;
    // A resized card snaps back: sliding its place while its size jumps would show the pinned corner
    // drifting and catching up. Only a card that was merely moved slides home.
    if (!prefersReducedMotion() && !sized) {
      setHoming(true);
      if (homingTimer.current !== null) window.clearTimeout(homingTimer.current);
      homingTimer.current = window.setTimeout(() => {
        homingTimer.current = null;
        setHoming(false);
      }, HOME_MS);
    }
    positions.clear(id);
    const Name = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
    announce(sized ? `${Name} back in its corner at its usual size` : `${Name} back in its corner`);
  }, [atHome, sized, positions, id, announce, name]);

  /** A card's left/top as fractions of the free travel a card of this size has in the layer now. */
  const fractionFor = useCallback(
    (left: number, top: number, w: number, h: number): CardFraction | null => {
      if (!layer) return null;
      const { width, height } = layer.getBoundingClientRect();
      return {
        fx: toFraction(left, width, w, inLeft, fraction.fx, inRight),
        fy: toFraction(top, height, h, inTop, fraction.fy, inBottom),
      };
    },
    [layer, fraction.fx, fraction.fy, inTop, inRight, inBottom, inLeft],
  );

  const commit = useCallback(
    (p: DragPosition) => {
      const slot = slotRef.current;
      const f = slot && fractionFor(p.x, p.y, slot.offsetWidth, slot.offsetHeight);
      if (f) positions.set(id, f);
    },
    [fractionFor, positions, id],
  );

  const drag = useDragPosition({
    targetRef: slotRef,
    boundsRef: layerRef,
    inset: INSET,
    position: layout?.at ?? { x: INSET, y: INSET },
    onCommit: commit,
    keyboard: KEY_STEP,
    onHome: sendHome,
  });

  // The corner plan's grip shows only once the stage has portalled a plan in (a twin with no floor
  // plan has none), and comes FIRST in the slot so it is the card's first tab stop (A1).
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [filled, setFilled] = useState(false);
  useLayoutEffect(() => {
    const target = targetRef.current;
    if (!target) return;
    const check = () => setFilled(target.childElementCount > 0);
    check();
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(check);
    observer.observe(target, { childList: true });
    return () => observer.disconnect();
  }, []);

  // Resizing (`observers-corner-plan-resize`). The size drawn is the stored one — or the default —
  // clamped to the limits for the picture as it is now, never written back (FR19).
  const canResize = resizable && filled && !!portalTargetProps;
  const layerBox = layout?.layer ?? { width: 0, height: 0 };
  const limits = sizeLimits(layerBox, INSET, defaultSize);
  const drawnSize = layout
    ? clampSize(storedSize ?? defaultSize, limits)
    : (storedSize ?? defaultSize);
  const commitResize = useCallback(
    (r: CardRect) => {
      // The position is re-committed against the NEW size: a fraction of the free travel the card
      // has now, so the pinned corner does not jump on the next measure (FR16).
      const f = fractionFor(r.left, r.top, r.w, r.h);
      if (f) positions.setPlacement(id, f, { w: r.w, h: r.h });
    },
    [fractionFor, positions, id],
  );
  const resize = useCornerResize({
    slotRef,
    layer,
    enabled: canResize,
    rect: layout
      ? { left: layout.at.x, top: layout.at.y, w: layout.size.w, h: layout.size.h }
      : null,
    limits,
    layerBox,
    inset: INSET,
    onCommit: commitResize,
  });
  const preview = resize.preview;
  const resizing = preview !== null;
  resizingRef.current = resizing;
  // The observer skipped every frame of the resize: measure once it ends, before paint — a card
  // whose stored fraction did not change (pinned at home, say) would otherwise keep its old size.
  useLayoutEffect(() => {
    if (!resizing) measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing]);
  /** The corner the cue shows: kept after the corner goes quiet, so its fade-out has a place. */
  const [cueCorner, setCueCorner] = useState<Corner>('bottom-left');
  if (resize.live && resize.live !== cueCorner) setCueCorner(resize.live);

  // While the grip has keyboard focus the outline shows too, so a keyboard mover sees home.
  const [gripFocused, setGripFocused] = useState(false);
  // Never while a corner is being dragged: the press leaves focus on a focused grip (FR15).
  const showOutline =
    !!layout && !resizing && (drag.isDragging || (gripFocused && (!atHome || sized)));
  // The outline marks where Home puts the card: for a resized card that is its default size at home,
  // not its current size (FR20).
  const homeOutline = layout
    ? sized
      ? (() => {
          // The width the slot will measure at home: its own max-width caps it on a narrow picture.
          const w = Math.min(defaultSize.w, layout.layer.width - 2 * INSET);
          const homeF = HOME_FRACTION[home];
          return {
            left: fromFraction(homeF.fx, layout.layer.width, w, inLeft, homeF.fx, inRight),
            top: fromFraction(
              homeF.fy,
              layout.layer.height,
              defaultSize.h,
              inTop,
              homeF.fy,
              inBottom,
            ),
            w,
            h: defaultSize.h,
          };
        })()
      : { left: layout.home.x, top: layout.home.y, w: layout.size.w, h: layout.size.h }
    : null;
  // While a corner is dragged, the grip's own keys and double-click do nothing: the press leaves a
  // focused grip focused, and a Home or an arrow mid-drag would be overwritten by the release.
  const { onKeyDown: gripKeyDown, onDoubleClick: gripDoubleClick } = drag.gripProps;
  const gripProps = {
    ...drag.gripProps,
    ...(gripKeyDown
      ? {
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
            // Only the keys that would move or reset the card are held back; Tab, Escape, P and
            // the rest go on as they always do.
            if (resizingRef.current && GRIP_MOVE_KEYS.has(e.key)) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            gripKeyDown(e);
          },
        }
      : {}),
    ...(gripDoubleClick
      ? {
          onDoubleClick: (e: React.MouseEvent<HTMLElement>) => {
            if (!resizingRef.current) gripDoubleClick(e);
          },
        }
      : {}),
  };

  const grip = (
    <Grip
      variant="ghost"
      size="xs"
      iconOnly
      aria-label={`Move ${name}`}
      aria-describedby={describedById}
      iconLeft={<Icon name="grip-vertical" variant="solid" />}
      className={`${gripPlacement === 'corner' ? 'is-corner' : ''} ${drag.isDragging ? 'is-dragging' : ''}`}
      data-card-grip={id}
      onFocus={e => setGripFocused(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setGripFocused(false)}
      {...gripProps}
    />
  );

  const placed = preview
    ? { left: preview.rect.left, top: preview.rect.top }
    : layout
      ? { left: layout.at.x, top: layout.at.y }
      : undefined;
  const targetSize = resizable
    ? preview
      ? { width: preview.rect.w, height: preview.rect.h }
      : { width: drawnSize.w, height: drawnSize.h }
    : undefined;

  return (
    <>
      {showOutline &&
        outlineHost &&
        homeOutline &&
        createPortal(
          <HomeOutline
            aria-hidden="true"
            data-home-outline={id}
            style={{
              left: homeOutline.left,
              top: homeOutline.top,
              width: homeOutline.w,
              height: homeOutline.h,
            }}
          />,
          outlineHost,
        )}
      <Slot
        ref={slotRef}
        data-movable-card={id}
        // Keys and presses on any part of a card — its grip included — are the card's, never the
        // picture's (FR8): the drawing area skips whatever is inside [data-control].
        data-control=""
        className={[
          placed ? '' : `home-${home}`,
          // A resize lifts the card exactly as a move does (FR15).
          drag.isDragging || resizing ? 'is-dragging' : '',
          homing ? 'is-homing' : '',
        ].join(' ')}
        data-resizable={resizable ? '' : undefined}
        data-corner-live={resize.live ?? undefined}
        data-resizing={resizing ? '' : undefined}
        data-min-height={minHeight ? '' : undefined}
        style={{
          ...placed,
          ...(minHeight ? { minHeight } : {}),
          ...(resize.live
            ? ({ '--corner-cursor': cursorFor(resize.live) } as React.CSSProperties)
            : {}),
        }}
      >
        {gripPlacement === 'corner' && filled && grip}
        {/* Each corner's 4 px outside band: the card paints over the rest of the square (FR1). */}
        {canResize &&
          CORNERS.map(corner => (
            <Rim key={corner} aria-hidden="true" data-corner-rim={corner} className={corner} />
          ))}
        {portalTargetProps && <div ref={targetRef} {...portalTargetProps} style={targetSize} />}
        {canResize && <CornerCue aria-hidden="true" data-corner={cueCorner} />}
        {children(gripPlacement === 'corner' ? null : grip)}
      </Slot>
    </>
  );
};

const Slot = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  /* The cards take pointers back themselves; the slot's own box (a gap, a margin) never covers
     the picture. */
  pointer-events: none;
  /* Its own size, not what is left of the layer after its left edge: a card that grows (the
     entity card's check sentence) grows, and the re-measure moves it back inside. */
  width: max-content;
  max-width: calc(100% - 2 * ${space[3]});

  /* Before the first measurement: the home corner, as camera-stage placed it. */
  &.home-top-left {
    top: ${space[3]};
    left: ${space[3]};
  }
  &.home-top-right {
    top: ${space[3]};
    right: ${space[3]};
  }
  &.home-bottom-left {
    bottom: ${space[3]};
    left: ${space[3]};
  }
  &.home-bottom-right {
    bottom: ${space[3]};
    right: ${space[3]};
  }

  /* Lifted above the other cards, inside the layer, only while it moves (FR7). */
  &.is-dragging {
    z-index: 1;
  }
  /* The design's lifted shadow (v3 L2120), on the card the slot holds — the plan arrives inside
     its portal target. */
  &.is-dragging > [data-control],
  &.is-dragging > [data-camera-corner] > [data-control] {
    box-shadow:
      0 0 16px 4px ${rgba(brand.frost, 0.9)},
      0 16px 32px rgba(${shadow.inkGlassRgb}, 0.35),
      inset 0 1px 0 ${rgba(surface[0], 0.7)};
  }
  &.is-homing {
    transition:
      left ${motion.durMid} ${motion.easeUi},
      top ${motion.durMid} ${motion.easeUi};
  }
  @media (prefers-reduced-motion: reduce) {
    &.is-homing {
      transition: none;
    }
  }
  /* A live corner's resize cursor, over everything in the card — the plan's own pan cursor too. */
  &[data-corner-live],
  &[data-corner-live] * {
    cursor: var(--corner-cursor) !important;
  }
`;

/**
 * A corner zone's outside band (`observers-corner-plan-resize`, FR1, FR5): 20 × 20 at the corner,
 * 4 px of it outside the card. Painted under the card, so only that band takes the pointer — and
 * as part of the slot (`data-control`), a press on it is never the picture's.
 */
const Rim = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  pointer-events: auto;
  &.top-left {
    left: -4px;
    top: -4px;
  }
  &.top-right {
    right: -4px;
    top: -4px;
  }
  &.bottom-left {
    left: -4px;
    bottom: -4px;
  }
  &.bottom-right {
    right: -4px;
    bottom: -4px;
  }
`;

const CUE = SELECTED_RAMP[6] as string; // --tl-ramp-primary-07, #1BB6D9
const AT: Record<Corner, string> = {
  'top-left': '0 0',
  'top-right': '100% 0',
  'bottom-left': '0 100%',
  'bottom-right': '100% 100%',
};
const cueFor = (corner: Corner) => `
  &[data-corner='${corner}']::before {
    background: radial-gradient(
      circle at ${AT[corner]},
      ${rgba(CUE, 0.42)} 0px,
      ${rgba(CUE, 0.22)} 12px,
      ${rgba(CUE, 0.08)} 26px,
      ${rgba(CUE, 0)} 44px
    );
  }
  &[data-corner='${corner}']::after {
    background: radial-gradient(circle at ${AT[corner]}, ${CUE} 0px, ${CUE} 8px, ${rgba(CUE, 0)} 40px);
  }
`;

/**
 * A live corner's cue (`observers-corner-plan-resize`, FR7–FR9; the design's Turn 2): the card's
 * edge turns brand cyan through the corner, fading along the edges, with a soft cyan glow inside.
 * The design blends the edge into the card's white border; the live corner plan has no border, so
 * here the cyan fades to nothing rather than adding a white edge that is not there at rest.
 */
const CornerCue = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${radius.lg};
  pointer-events: none;
  opacity: 0;
  transition: opacity ${motion.durFast} ${motion.easeUi};
  [data-corner-live] > & {
    opacity: 1;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  &::before,
  &::after {
    content: '';
    position: absolute;
    box-sizing: border-box;
  }
  /* The glow, inside the edge. */
  &::before {
    inset: 2px;
    border-radius: calc(${radius.lg} - 2px);
  }
  /* The edge: a 2 px ring, masked out of a fill. */
  &::after {
    inset: 0;
    padding: 2px;
    border-radius: ${radius.lg};
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#000 0 0) content-box exclude,
      linear-gradient(#000 0 0);
  }
  ${CORNERS.map(cueFor).join('')}
`;

const Grip = styled(Button)`
  pointer-events: auto;
  cursor: grab;
  touch-action: none;
  flex: none;
  color: ${ink[4]};
  &.is-dragging {
    cursor: grabbing;
  }
  /* The corner plan's grip: a white .9 disc with a soft ink shadow at the plan's top-left (v3 L1746). */
  &.is-corner {
    position: absolute;
    top: 6px;
    left: 6px;
    z-index: 1;
    border-radius: ${radius.pill};
    background: ${rgba(surface[0], 0.9)};
    box-shadow: 0 1px 3px rgba(${shadow.inkGlassRgb}, 0.15);
  }
`;

/** The card's home while it is away (v3 L1710): white .85 dashed, a white .10 fill. */
const HomeOutline = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 2px dashed ${rgba(surface[0], 0.85)};
  background: ${rgba(surface[0], 0.1)};
  border-radius: ${radius.lg};
  pointer-events: none;
`;
